"use client";

import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Instagram, Music, Facebook, Pin, Loader, CreditCard } from "lucide-react";
function FooterNavConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <ul className="space-y-3">
        <li className="h-4 w-24 bg-muted animate-pulse rounded-md" />
        <li className="h-4 w-20 bg-muted animate-pulse rounded-md" />
        <li className="h-4 w-28 bg-muted animate-pulse rounded-md" />
        <li className="h-4 w-16 bg-muted animate-pulse rounded-md" />
      </ul>;
  }
  return <ul className="space-y-3">
      {menuItems.map((item, i) => <li data-index={i} key={item.id}>
          <Link to={item.href || "#"} className="text-foreground/75 hover:text-accent transition-colors font-default text-lg">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
function NewsletterForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="py-2">
        <p className="text-accent font-mono text-xs uppercase tracking-widest">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="flex flex-col sm:flex-row gap-2">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email"
        }
      }} render={({
        field
      }) => <FormItem className="flex-1">
              <FormControl>
                <Input type="email" placeholder="Enter your email" className="bg-transparent border-foreground/20 focus-visible:ring-accent rounded-md h-12" {...field} />
              </FormControl>
              <FormMessage className="text-[10px] uppercase tracking-tighter" />
            </FormItem>} />
        <Button type="submit" disabled={isSubmitting} className="h-12 px-8 bg-accent hover:bg-accent/90 text-accent-foreground font-mono text-xs uppercase tracking-widest rounded-md transition-all">
          {isSubmitting ? <Loader className="animate-spin size-4" /> : "Subscribe"}
        </Button>
      </div>
      {submitError && <p className="text-destructive text-[10px] uppercase mt-2">{submitError}</p>}
    </Form>;
}
export default function Footer() {
  const addressLines = ["42 Fashion District", "Los Angeles, California", "90014", "United States"];
  const connectLinks = [{
    label: "Instagram",
    href: "#"
  }, {
    label: "TikTok",
    href: "#"
  }, {
    label: "Newsletter",
    href: "#"
  }];
  const hours = [{
    label: "Support",
    value: "09:00 — 18:00 Daily"
  }, {
    label: "Orders",
    value: "24 / 7 Online"
  }];
  const legalLinks = [{
    label: "Privacy",
    href: "/PrivacyPolicy"
  }, {
    label: "Terms",
    href: "/TermsOfService"
  }, {
    label: "Returns",
    href: "/ShippingReturns"
  }];
  const socialIcons = [{
    icon: Instagram,
    label: "Instagram",
    href: "#"
  }, {
    icon: Music,
    label: "TikTok",
    href: "#"
  }, {
    icon: Facebook,
    label: "Facebook",
    href: "#"
  }, {
    icon: Pin,
    label: "Pinterest",
    href: "#"
  }];
  const paymentMethods = ["Visa", "Mastercard", "PayPal", "Apple Pay"];
  return <section data-section-id="4474" id="site-footer" className="dark bg-background text-foreground pt-32 md:pt-40 pb-16 px-6 md:px-12 lg:px-16">
      <div className="max-w-7xl mx-auto">
        <div className="mb-16 md:mb-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-end">
            <div>
              <h2 className="font-default font-bold text-foreground text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.9] tracking-tighter mb-8">
                LUSH — A digital
                <br />
                atelier for the <em className="italic font-light">modern muse.</em>
              </h2>
            </div>
            <div className="space-y-6">
              <p className="font-mono text-xs uppercase tracking-widest text-foreground/50">
                Join the Atelier — Exclusive updates & collections
              </p>
              <FormProvider formId="newsletter-footer" sectionName="footer" defaultValues={{
              email: ""
            }} successMessage="Welcome to the inner circle.">
                <NewsletterForm />
              </FormProvider>
            </div>
          </div>
        </div>

        <Separator className="bg-border opacity-40 mb-16 md:mb-20" />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-12 lg:gap-8 mb-20 md:mb-24">
          {}
          <div className="xl:col-span-2">
            <div className="flex items-start gap-3 mb-8">
              <Link to="/" aria-label="Lush Fashion Home" className="inline-block">
                <WvcLogo className="h-7 w-auto" />
              </Link>
              <span aria-hidden="true" className="inline-block w-2 h-2 bg-accent mt-2" />
            </div>
            <address className="not-italic font-mono text-xs uppercase tracking-widest text-foreground/75 space-y-2">
              {addressLines.map((line, i) => <div data-index={i} key={i}>
                  {line}
                </div>)}
            </address>
          </div>

          {}
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-foreground/50 mb-6">
              Navigate
            </p>
            <MenuProvider menu_id="23">
              <FooterNavConsumer />
            </MenuProvider>
          </div>

          {}
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-foreground/50 mb-6">
              Support
            </p>
            <ul className="space-y-4 mb-6">
              {hours.map((item, i) => <li data-index={i} key={item.label}>
                  <div className="font-mono text-xs uppercase tracking-widest text-foreground/50 mb-1">
                    {item.label}
                  </div>
                  <div className="font-default text-base text-foreground/75">
                    {item.value}
                  </div>
                </li>)}
            </ul>
            <Link to="/CustomerCarePage" className="font-mono text-xs uppercase tracking-widest text-foreground/75 hover:text-accent transition-colors inline-flex items-center gap-2">
              Contact Us &rarr;
            </Link>
          </div>

          {}
          <div className="xl:col-span-2">
            <p className="font-mono text-xs uppercase tracking-widest text-foreground/50 mb-6">
              Connect
            </p>
            <div className="space-y-8">
              <ul className="flex gap-6">
                {socialIcons.map((social, i) => <li data-index={i} key={social.label}>
                    <Link to={social.href} aria-label={social.label} className="text-foreground/75 hover:text-accent transition-all transform hover:-translate-y-1 block">
                      <social.icon className="size-5 stroke-[1.25px]" />
                    </Link>
                  </li>)}
              </ul>
              <div>
                <Link to="mailto:hello@lushfashion.com" className="font-mono text-xs uppercase tracking-widest text-foreground/75 hover:text-accent transition-colors block mb-4">
                  hello@lushfashion.com
                </Link>
                <div className="flex flex-wrap gap-4 items-center">
                  <CreditCard className="size-4 text-foreground/30" />
                  <div className="flex gap-3">
                    {paymentMethods.map((method, i) => <span data-index={i} key={method} className="font-mono text-[9px] uppercase tracking-widest text-foreground/40 border border-foreground/10 px-2 py-1 rounded-md">
                        {method}
                      </span>)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Separator className="bg-border opacity-40 mb-8" />

        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <p className="font-mono text-foreground/50 uppercase tracking-widest" style={{
          fontSize: "10px"
        }}>
            &copy; 2024 LUSH FASHION &middot; All rights reserved &middot; Curated with precision
          </p>
          <ul className="flex flex-wrap items-center gap-x-6 gap-y-2">
            {legalLinks.map((link, i) => <li data-index={i} key={link.label}>
                <Link to={link.href} className="font-mono text-foreground/50 hover:text-accent transition-colors uppercase tracking-widest" style={{
              fontSize: "10px"
            }}>
                  {link.label}
                </Link>
              </li>)}
          </ul>
        </div>
      </div>
    </section>;
}